import Plot from 'react-plotly.js';
import { useMemo } from 'react';
// import { E3M3_TO_MCF, M3_TO_BBL, M_TO_FT } from '../helpers/conversionFactors';
import { chartColors } from '../helpers/chartColors';
import { useSelector } from 'react-redux';
import { selectUser } from '../reducers/userSlice';
import { selectOptions } from '../reducers/optionsSlice';
import { convertVolume } from '../helpers/convertVolume';

const MONTH_LENGTH = 30.436875;

export const WaterfloodChart = ({ well }) => {

  const user = useSelector(selectUser);
  const options = useSelector(selectOptions);

  const units = options?.units || "metric";

  const darkMode = user?.value?.darkMode;

  const data = useMemo(() => getChartData(well), [well, darkMode, options]); //eslint-disable-line
  
  const orientation = options?.chartOrientation || "Horizontal";

  function getChartData(well) {
    try {

      if (!well) return [];

      // const 

      // add traces
      let oilCutTraceTime = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x1',
        yaxis: 'y1',
        name: `Oil Cut (%)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[0]
          },
          symbol: 'circle',
          size: 4
        },
        line: {
          width: 0
        }
      };
      let oilCutTraceCum = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x2',
        yaxis: 'y3',
        name: `Oil Cut (%)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[0]
          },
          symbol: 'circle',
          size: 4
        },
        line: {
          width: 0
        },
        showlegend: false
      };
      let totalFluidCumOilTraceTime = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x1',
        yaxis: 'y2',
        name: `Fluid Rate (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[1]
          },
          symbol: 'square',
          size: 4
        },
        line: {
          width: 0
        }
      };
      let totalFluidCumOilTraceCum = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x2',
        yaxis: 'y4',
        name: `Fluid Rate (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[1]
          },
          symbol: 'square',
          size: 4
        },
        line: {
          width: 0
        },
        showlegend: false
      };
      let oilCutFitTraceP50Time = {
        x: [],
        y: [],
        mode: 'line',
        type: 'scatter',
        xaxis: 'x1',
        yaxis: 'y1',
        name: `Oil Cut P50 (%)`,
        line: {
          color: chartColors[0],
          width: 3
        }
      };
      let oilCutFitTraceP10Time = {
        x: [],
        y: [],
        mode: 'line',
        type: 'scatter',
        fill: 'tonexty',
        xaxis: 'x1',
        yaxis: 'y1',
        name: `Oil Cut P10 (%)`,
        showlegend: false,
        line: {
          width: 2,
          color: chartColors[0],
          dash: 'dot'
        },
      };
      let oilCutFitTraceP90Time = {
        x: [],
        y: [],
        mode: 'line',
        type: 'scatter',
        fill: 'topreviousy',
        xaxis: 'x1',
        yaxis: 'y1',
        name: `Oil Cut P90 (%)`,
        showlegend: false,
        line: {
          width: 2,
          color: chartColors[0],
          dash: 'dot'
        },
      };
      let oilCutFitTraceP50Cum = {
        x: [],
        y: [],
        mode: 'line',
        type: 'scatter',
        xaxis: 'x2',
        yaxis: 'y3',
        name: `Oil Cut P50 (%)`,
        line: {
          color: chartColors[0],
          width: 3
        },
        showlegend: false
      };
      let oilCutFitTraceP10Cum = {
        x: [],
        y: [],
        mode: 'line',
        type: 'scatter',
        fill: 'tonexty',
        xaxis: 'x2',
        yaxis: 'y3',
        name: `Oil Cut P10 (%)`,
        showlegend: false,
        line: {
          width: 2,
          color: chartColors[0],
          dash: 'dot'
        },
      };
      let oilCutFitTraceP90Cum = {
        x: [],
        y: [],
        mode: 'line',
        type: 'scatter',
        fill: 'topreviousy',
        xaxis: 'x2',
        yaxis: 'y3',
        name: `Oil Cut P90 (%)`,
        showlegend: false,
        line: {
          width: 2,
          color: chartColors[0],
          dash: 'dot'
        },
      };
      
      let oilRateTraceTime = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x3',
        yaxis: 'y5',
        name: `Oil Rate (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[4]
          },
          symbol: 'diamond',
          size: 4
        },
      };
      let oilRateTraceCum = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x4',
        yaxis: 'y7',
        name: `Oil Rate (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[4]
          },
          symbol: 'diamond',
          size: 4
        },
        showlegend: false
      };
      let fluidRateTraceTime = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x3',
        yaxis: 'y6',
        name: `Fluid Rate (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[1]
          },
          symbol: 'square',
          size: 4
        },
      };
      let fluidRateTraceCum = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x4',
        yaxis: 'y8',
        name: `Fluid Rate (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[1]
          },
          symbol: 'square',
          size: 4
        },
        showlegend: false
      };
      let forecastOilRateTraceP50Time = {
        x: [],
        y: [],
        mode: 'line',
        type: 'scatter',
        // fill: 'tonexty',
        xaxis: 'x3',
        yaxis: 'y5',
        name: `Oil Rate P50 (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        line: {
          width: 3,
          color: chartColors[4]
        },
      };
      let forecastOilRateTraceP10Time = {
        x: [],
        y: [],
        mode: 'line',
        type: 'scatter',
        fill: 'tonexty',
        xaxis: 'x3',
        yaxis: 'y5',
        name: `Oil Rate P10 (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        showlegend: false,
        line: {
          width: 2,
          color: chartColors[4],
          dash: 'dot'
        },
      };
      let forecastOilRateTraceP90Time = {
        x: [],
        y: [],
        mode: 'line',
        type: 'scatter',
        fill: 'topreviousy',
        xaxis: 'x3',
        yaxis: 'y5',
        name: `Oil Rate P90 (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        showlegend: false,
        line: {
          width: 2,
          color: chartColors[4],
          dash: 'dot'
        },
      };
      let forecastOilRateTraceP50Cum = {
        x: [],
        y: [],
        mode: 'line',
        type: 'scatter',
        // fill: 'tonexty',
        xaxis: 'x4',
        yaxis: 'y7',
        name: `Oil Rate P50 (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        line: {
          width: 3,
          color: chartColors[4]
        },
        showlegend: false
      };
      let forecastOilRateTraceP10Cum = {
        x: [],
        y: [],
        mode: 'line',
        type: 'scatter',
        fill: 'tonexty',
        xaxis: 'x4',
        yaxis: 'y7',
        name: `Oil Rate P10 (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        showlegend: false,
        line: {
          width: 2,
          color: chartColors[4],
          dash: 'dot'
        },
      };
      let forecastOilRateTraceP90Cum = {
        x: [],
        y: [],
        mode: 'line',
        type: 'scatter',
        fill: 'topreviousy',
        xaxis: 'x4',
        yaxis: 'y7',
        name: `Oil Rate P90 (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        showlegend: false,
        line: {
          width: 2,
          color: chartColors[4],
          dash: 'dot'
        },
      };


      well?.production?.byFluid?.oil?.forEach((f,i) => {
        if (well?.production?.byFluid?.oil?.length !== well?.production?.byFluid?.water?.length) {
          console.log('Warning! Oil and water production have different lengths!');
        };
        
        if (f.volume > 0.1) {
          const waterRate = (well?.production?.byFluid?.water?.filter(p => p.monthsOnline >= f.monthsOnline)?.[0]?.volume || 0);
          oilCutTraceTime.x.push(f.monthsOnline);
          oilCutTraceTime.y.push((f.volume / (waterRate + f.volume)) * 100);
          oilCutTraceCum.x.push(convertVolume(f.cumVolume, options.units, 'oil'));
          oilCutTraceCum.y.push((f.volume / (waterRate + f.volume)) * 100);
          totalFluidCumOilTraceTime.x.push(f.monthsOnline);
          totalFluidCumOilTraceTime.y.push(convertVolume((f.volume + waterRate), options.units, 'oil') / MONTH_LENGTH);
          totalFluidCumOilTraceCum.x.push(convertVolume(f.cumVolume, options.units, 'oil'));
          totalFluidCumOilTraceCum.y.push(convertVolume((f.volume + waterRate), options.units, 'oil') / MONTH_LENGTH);
          fluidRateTraceTime.x.push(f.monthsOnline);
          fluidRateTraceTime.y.push(convertVolume((f.volume + waterRate), options.units, 'oil') / MONTH_LENGTH);
          fluidRateTraceCum.x.push(convertVolume(f.cumVolume, options.units, 'oil'));
          fluidRateTraceCum.y.push(convertVolume((f.volume + waterRate), options.units, 'oil') / MONTH_LENGTH);
          oilRateTraceTime.x.push(f.monthsOnline);
          oilRateTraceTime.y.push(convertVolume(f.volume, options.units, 'oil') / MONTH_LENGTH);
          oilRateTraceCum.x.push(convertVolume(f.cumVolume, options.units, 'oil'));
          oilRateTraceCum.y.push(convertVolume(f.volume, options.units, 'oil') / MONTH_LENGTH);
        };
      });

      well?.forecast?.byFluid?.oil?.forEach(f => {
        oilCutFitTraceP90Time.x.push(f.monthsOnline);
        oilCutFitTraceP90Time.y.push(f.oilCut?.p90);
        oilCutFitTraceP50Time.x.push(f.monthsOnline);
        oilCutFitTraceP50Time.y.push(f.oilCut?.p50);
        oilCutFitTraceP10Time.x.push(f.monthsOnline);
        oilCutFitTraceP10Time.y.push(f.oilCut?.p10);
        oilCutFitTraceP90Cum.x.push(convertVolume(f.cumVolume?.p90, options.units, 'oil'));
        oilCutFitTraceP90Cum.y.push(f.oilCut?.p90);
        oilCutFitTraceP50Cum.x.push(convertVolume(f.cumVolume?.p50, options.units, 'oil'));
        oilCutFitTraceP50Cum.y.push(f.oilCut?.p50);
        oilCutFitTraceP10Cum.x.push(convertVolume(f.cumVolume?.p10, options.units, 'oil'));
        oilCutFitTraceP10Cum.y.push(f.oilCut?.p10);
        forecastOilRateTraceP90Time.x.push(f.monthsOnline);
        forecastOilRateTraceP90Time.y.push(convertVolume(f.volume?.p90, options.units, 'oil') / MONTH_LENGTH);
        forecastOilRateTraceP50Time.x.push(f.monthsOnline);
        forecastOilRateTraceP50Time.y.push(convertVolume(f.volume?.p50, options.units, 'oil') / MONTH_LENGTH);
        forecastOilRateTraceP10Time.x.push(f.monthsOnline);
        forecastOilRateTraceP10Time.y.push(convertVolume(f.volume?.p10, options.units, 'oil') / MONTH_LENGTH);
        forecastOilRateTraceP90Cum.x.push(convertVolume(f.cumVolume?.p50, options.units, 'oil'));
        forecastOilRateTraceP90Cum.y.push(convertVolume(f.volume?.p90, options.units, 'oil') / MONTH_LENGTH);
        forecastOilRateTraceP50Cum.x.push(convertVolume(f.cumVolume?.p50, options.units, 'oil'));
        forecastOilRateTraceP50Cum.y.push(convertVolume(f.volume?.p50, options.units, 'oil') / MONTH_LENGTH);
        forecastOilRateTraceP10Cum.x.push(convertVolume(f.cumVolume?.p50, options.units, 'oil'));
        forecastOilRateTraceP10Cum.y.push(convertVolume(f.volume?.p10, options.units, 'oil') / MONTH_LENGTH);
      });

      return [
        oilCutTraceTime, 
        oilCutTraceCum, 
        oilCutFitTraceP50Time, 
        oilCutFitTraceP90Time, 
        oilCutFitTraceP10Time, 
        oilCutFitTraceP50Cum, 
        oilCutFitTraceP90Cum, 
        oilCutFitTraceP10Cum, 
        totalFluidCumOilTraceTime, 
        totalFluidCumOilTraceCum, 
        fluidRateTraceTime, 
        fluidRateTraceCum, 
        oilRateTraceTime, 
        oilRateTraceCum, 
        forecastOilRateTraceP50Time, 
        forecastOilRateTraceP90Time, 
        forecastOilRateTraceP10Time,
        forecastOilRateTraceP50Cum, 
        forecastOilRateTraceP90Cum, 
        forecastOilRateTraceP10Cum
      ];
    } catch (error) {
      console.log(error);
      return [];
    };
  };

  const layout = {
    grid: (orientation === 'Horizontal' ? {rows: 2, columns: 2, pattern: 'independent'} : {rows: 4, columns: 1, pattern: 'independent'}),
    // grid: (orientation === 'Horizontal' ? {rows: 1, columns: 2, pattern: 'independent'} : {rows: 2, columns: 1, pattern: 'independent'}),
    yaxis: { // Oil cut y axis time
      linecolor: chartColors[0],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[0],
      title: {
        text: `Oil Cut (%)`,
        font: {
          size: 12
        }
      },
      // autorange: true,
      // range: [0, 100],
      type: 'log',
      anchor: 'x1',
      rangemode: 'nonnegative',
      domain: (orientation === 'Horizontal' ? [0.55, 1] : [0.75, 1])
    },
    yaxis2: { // Fluid rate y axis time
      linecolor: chartColors[1],
      showgrid: false,
      color: chartColors[1],
      title: {
        text: `Fluid Rate (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        font: {
          size: 12
        }
      },
      autorange: true,
      rangemode: 'nonnegative',
      type: 'log',
      domain: (orientation === 'Horizontal' ? [0.55, 1] : [0.75, 1]),
      overlaying: 'y1',
      side: 'right',
      anchor: 'x1',
    },
    xaxis: { // Oil cut x axis time
      // showgrid: false,
      linecolor: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'grey',
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Months Online',
        standoff: 10,
        font: {
          size: 12
        }
      },
      anchor: 'y1',
      domain: (orientation === 'Horizontal' ? [0, 0.425] : [0, 1]),
      // domain: [0, 0.425],
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
      // showspikes: true,
      // spikemode: 'toaxis' // or 'across' or 'marker'
    },
    yaxis3: { // Oil cut y axis cum
      linecolor: chartColors[0],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[0],
      title: {
        text: `Oil Cut (%)`,
        font: {
          size: 12
        }
      },
      autorange: true,
      type: 'log',
      anchor: 'x2',
      rangemode: 'nonnegative',
      domain: (orientation === 'Horizontal' ? [0, 0.45] : [0.5, 0.725])
    },
    yaxis4: { // Fluid rate y axis cum
      linecolor: chartColors[1],
      showgrid: false,
      color: chartColors[1],
      title: {
        text: `Fluid Rate (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        font: {
          size: 12
        }
      },
      autorange: true,
      rangemode: 'nonnegative',
      type: 'log',
      domain: (orientation === 'Horizontal' ? [0, 0.45] : [0.5, 0.725]),
      overlaying: 'y3',
      side: 'right',
      anchor: 'x2',
    },
    xaxis2: { // Oil cut x axis cum
      // showgrid: false,
      linecolor: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'grey',
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: `Cumulative Oil (${units === 'metric' ? 'm3' : 'bbl'})`,
        standoff: 10,
        font: {
          size: 12
        }
      },
      anchor: 'y3',
      domain: (orientation === 'Horizontal' ? [0, 0.425] : [0, 1]),
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
      // showspikes: true,
      // spikemode: 'toaxis' // or 'across' or 'marker'
    },
    yaxis5: { // Oil rate y axis
      linecolor: chartColors[4],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[4],
      title: {
        text: `Oil Rate (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        font: {
          size: 12
        },
        // standoff: 10
      },
      type: 'log',
      autorange: true,
      rangemode: 'nonnegative',
      anchor: 'x3',
      domain: (orientation === 'Horizontal' ? [0.55, 1] : [0.25, 0.475])
    },
    yaxis6: { // Fluid rate y axis
      linecolor: chartColors[1],
      showgrid: false,
      color: chartColors[1],
      title: {
        text: `Fluid Rate (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        font: {
          size: 12
        }
      },
      autorange: true,
      rangemode: 'nonnegative',
      type: 'log',
      domain: (orientation === 'Horizontal' ? [0.55, 1] : [0.25, 0.475]),
      overlaying: 'y5',
      side: 'right',
      anchor: 'x3',
    },
    xaxis3: { // Fluid rate x axis
      linecolor: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'grey',
      title: {
        text: `Months Online`,
        standoff: 10,
        font: {
          size: 12
        },
      },
      autorange: true,
      rangemode: 'nonnegative',
      // domain: [0.575, 1],
      domain: (orientation === 'Horizontal' ? [0.575, 1] : [0, 1]),
      anchor: 'y5',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    yaxis7: { // Oil rate y axis
      linecolor: chartColors[4],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[4],
      title: {
        text: `Oil Rate (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        font: {
          size: 12
        },
        // standoff: 10
      },
      type: 'log',
      autorange: true,
      rangemode: 'nonnegative',
      anchor: 'x4',
      domain: (orientation === 'Horizontal' ? [0, 0.45] : [0, 0.225])
    },
    yaxis8: { // Fluid rate y axis
      linecolor: chartColors[1],
      showgrid: false,
      color: chartColors[1],
      title: {
        text: `Fluid Rate (${units === 'metric' ? 'm3' : 'bbl'}/d)`,
        font: {
          size: 12
        }
      },
      autorange: true,
      rangemode: 'nonnegative',
      type: 'log',
      domain: (orientation === 'Horizontal' ? [0, 0.45] : [0, 0.225]),
      overlaying: 'y7',
      side: 'right',
      anchor: 'x4',
    },
    xaxis4: { // Fluid rate x axis
      linecolor: darkMode ? 'lightgrey' : 'grey',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'grey',
      title: {
        text: `Cumulative Oil (${units === 'metric' ? 'm3' : 'bbl'})`,
        standoff: 10,
        font: {
          size: 12
        },
      },
      autorange: true,
      rangemode: 'nonnegative',
      // domain: [0.575, 1],
      domain: (orientation === 'Horizontal' ? [0.575, 1] : [0, 1]),
      anchor: 'y7',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    hovermode: "x",
    hoverdistance: 100, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      font: {
        color: darkMode ? 'lightgrey' : 'grey'
      },
      orientation: "h",
      yanchor: "top",
      yref: 'paper',
      y: orientation === 'Horizontal' ? -0.05 : -0.025,
    },
    uirevision:'true',
    shapes: [
      { // cutoff line time
        type: 'line',
        xref: 'paper',
        yref: 'y5',
        x0: orientation === 'Horizontal' ? 0.575 : 0,
        y0: convertVolume(well?.params?.waterflood?.cutoff, options.units, 'oil') / MONTH_LENGTH,
        x1: 1,
        y1: convertVolume(well?.params?.waterflood?.cutoff, options.units, 'oil') / MONTH_LENGTH,
        opacity: 0.75,
        line:{
          color: '#333',
          width: 2,
          dash:'dot'
        }
      },
      { // cutoff line cum
        type: 'line',
        xref: 'paper',
        yref: 'y7',
        x0: orientation === 'Horizontal' ? 0.575 : 0,
        y0: convertVolume(well?.params?.waterflood?.cutoff, options.units, 'oil') / MONTH_LENGTH,
        x1: 1,
        y1: convertVolume(well?.params?.waterflood?.cutoff, options.units, 'oil') / MONTH_LENGTH,
        opacity: 0.75,
        line:{
          color: '#333',
          width: 2,
          dash:'dot'
        }
      },
      { // maxFluidRate line time
        type: 'line',
        xref: 'paper',
        yref: 'y6',
        x0: orientation === 'Horizontal' ? 0.575 : 0,
        y0: convertVolume((well?.params?.waterflood?.maxFluidRate || 500), options.units, 'oil') / MONTH_LENGTH,
        x1: 1,
        y1: convertVolume((well?.params?.waterflood?.maxFluidRate || 500), options.units, 'oil') / MONTH_LENGTH,
        opacity: 0.75,
        line:{
          color: chartColors[0],
          width: 2,
          dash:'dot'
        }
      },
      { // maxFluidRate line cum
        type: 'line',
        xref: 'paper',
        yref: 'y8',
        x0: orientation === 'Horizontal' ? 0.575 : 0,
        y0: convertVolume((well?.params?.waterflood?.maxFluidRate || 500), options.units, 'oil') / MONTH_LENGTH,
        x1: 1,
        y1: convertVolume((well?.params?.waterflood?.maxFluidRate || 500), options.units, 'oil') / MONTH_LENGTH,
        opacity: 0.75,
        line:{
          color: chartColors[0],
          width: 2,
          dash:'dot'
        }
      },
    ],
    // annotations: [
    //   {
    //     text: "Oil Cut",
    //       font: {
    //       size: 16,
    //        color: darkMode ? 'lightgrey' : 'grey'
    //     },
    //     showarrow: false,
    //     align: 'center',
    //     x: orientation === 'Horizontal' ? 0.175 : 0.5, //position in x domain
    //     y: orientation === 'Horizontal' ? 1.05 : 1.02, //position in y domain
    //     xref: 'paper',
    //     yref: 'paper',
    //   },
    //   {
    //     text: "Fluid Rate",
    //       font: {
    //       size: 16,
    //        color: darkMode ? 'lightgrey' : 'grey'
    //     },
    //     showarrow: false,
    //     align: 'center',
    //     x: orientation === 'Horizontal' ? 0.85 : 0.5, //position in x domain
    //     y: orientation === 'Horizontal' ? 1.05 : 0.475, //position in y domain
    //     xref: 'paper',
    //     yref: 'paper',
    //   }
    // ],
    margin: {"t": 50, "l": 50, "r": 50, "b": 30},
    images: [
      {
        x: orientation === 'Horizontal' ? 0.065 : 0.065,
        y: orientation === 'Horizontal' ? 1.015 : 1.0075,
        sizex: 0.1,
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_${darkMode ? 'white' : 'primary'}.png`,
        xref: "paper",
        yref: "paper",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 1
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: (darkMode ? 'transparent' : '#fff'),
    title: well.uwi || well.name || 'Waterflood Chart'
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: well?.uwi + "-waterflood-" + (new Date().toISOString().split('T')[0]),
      height: orientation === 'Horizontal' ? 480 : 960,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%', minWidth: '600px'}}
      // style={{width: '100%', height: '100%', minHeight: orientation === 'Horizontal' ? '480px' : '960px', minWidth: '600px'}}
      // onClick={(e) => handlePlotClick(e)}
    />
  );

  
  // function handlePlotClick(e) {
  //   console.log(e)
  //   console.log(e.event?.pointerY)
  // };
};
